import { AccountsReceivableReportSource } from "api";
import { Transition } from "components/shared/column";
import Dropdown from "components/shared/dropdown";
import { dateToView, utcTimeInOffset } from "helpers/date";
import { isHidden } from "helpers/hiddenData";
import { ending } from "helpers/i18n";
import {
    normalizeSettlementTableState,
    readSettlementState,
    settlementModeChange,
    settlementServerFilters,
    settlementStorageKey,
    writeSettlementState,
} from "helpers/mutualSettlements";
import { viewContainer } from "helpers/redux";
import { REPORT_TYPES } from "helpers/reports";
import isEqual from "lodash/isEqual";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    accountsReceivableReportDemo,
    download,
    getGeneratedReport,
    getItem as loadReport,
    getQueryByReportName,
} from "sources/books/reports";
import ReportTaskGate from "../reportTaskGate";
import SettlementTable from "../show/markup/accountsReceivableReport";
import ReportDataControls from "../show/reportDataControls";
import SettlementFilters from "./filters";

function settingsFor(props, demoData) {
    const saved = readSettlementState(
        sessionStorage,
        settlementStorageKey(props.profile.id, props.profile.organizationId, demoData),
    );
    const settings = {
        startDate: moment().startOf("day").subtract(14, "days"),
        endDate: moment().endOf("day").add(1, "month"),
        source: AccountsReceivableReportSource.OwnFleet,
        isAccountsPayable: false,
        groupByDates: false,
        groupByCounterparties: false,
        onlyOverdue: false,
        previousDayPaymentsOnly: false,
        ...saved,
        demoData,
    };
    settings.startDate = moment(settings.startDate);
    settings.endDate = moment(settings.endDate);
    settings.draftPeriod = { startDate: settings.startDate.clone(), endDate: settings.endDate.clone() };
    settings.settlementTableState = normalizeSettlementTableState(
        {
            filters: [],
            sortBy: [],
            pageIndex: 0,
            pageSize: 30,
            ...saved.settlementTableState,
        },
        demoData ? { source: AccountsReceivableReportSource.OwnFleet, isAccountsPayable: false } : settings,
    );
    return settings;
}

/** Own the settlement lifecycle independently of generic reports and Book record editing. */
export function MutualSettlementsPage(props) {
    const [state, setState] = useState(() => ({
        ...settingsFor(props, false),
        data: { name: "Взаиморасчёты", data: [], columns: [] },
        pending: true,
        acceptedSignature: null,
        publication: null,
        error: "",
        tableRevision: 0,
        requestVersion: 0,
        upcomingInfo: { count: 0, days: 3, loading: true, error: "" },
        upcomingPanelRequest: 0,
    }));
    const update = useCallback(
        (patch) =>
            setState((previous) => ({
                ...previous,
                ...(typeof patch === "function" ? patch(previous) : patch),
            })),
        [],
    );
    const requestIdRef = useRef(0);
    const signatureRef = useRef(null);
    const loadRef = useRef(null);
    const printState = useRef();
    const initialTaskKey = useRef(props.taskKey);
    const initialRequest = useRef(true);
    const reload = useCallback((taskKey) => loadRef.current(taskKey), []);
    const effectiveState = () =>
        state.demoData
            ? { ...state, source: AccountsReceivableReportSource.OwnFleet, isAccountsPayable: false }
            : state;
    const requestParams = () => {
        const state = effectiveState();
        return {
            id: state.isAccountsPayable ? REPORT_TYPES.AccountsPayableReport : REPORT_TYPES.AccountsReceivableReport,
            source: state.source,
            startDate: utcTimeInOffset(state.startDate, props.organizationTimezone.utcOffset),
            endDate: utcTimeInOffset(state.endDate, props.organizationTimezone.utcOffset),
            groupByDates: state.groupByDates,
            groupByCounterparties: state.groupByCounterparties,
            onlyOverdue: state.onlyOverdue,
            previousDayPaymentsOnly: state.previousDayPaymentsOnly,
            ...(!state.demoData ? settlementServerFilters(state.settlementTableState.filters, state) : {}),
        };
    };
    const signature = () => {
        const params = requestParams();
        return `${state.demoData}|${params.id}|${getQueryByReportName(params)}`;
    };
    const dataReady = () => !state.pending && state.acceptedSignature === signature();
    const outputReady = () =>
        dataReady() &&
        state.publication?.response === state.data.data &&
        isEqual(state.publication.filters, state.settlementTableState.filters);

    const load = async (taskKey) => {
        const requestId = ++requestIdRef.current;
        const isInitialRequest = initialRequest.current;
        initialRequest.current = false;
        const requestedSignature = signature();
        const params = requestParams();
        const { demoData, startDate, endDate } = state;
        update({ pending: true, error: "", acceptedSignature: null, publication: null });
        try {
            const data = await (demoData
                ? accountsReceivableReportDemo()
                : taskKey
                  ? getGeneratedReport(taskKey)
                  : loadReport(params));
            if (requestId !== requestIdRef.current) {
                return;
            }
            if (requestedSignature !== signatureRef.current) {
                update({ pending: false });
                return;
            }
            if (!data) {
                throw new Error("Не удалось загрузить отчёт. Повторите запрос.");
            }
            if (isInitialRequest && !demoData && !taskKey && Array.isArray(data.data) && data.data.length === 0) {
                changeDemoData(true);
                return;
            }
            if (taskKey) {
                props.removeBgTask({ key: taskKey });
            }
            update((state) => ({
                data: { ...data, startDate, endDate },
                pending: false,
                acceptedSignature: requestedSignature,
                dataLoadedAt: (state.dataLoadedAt || 0) + 1,
            }));
        } catch {
            if (requestId === requestIdRef.current) {
                update({ pending: false, error: "Не удалось загрузить отчёт. Повторите запрос." });
            }
        }
    };
    const onTableState = (settlementTableState) => {
        if (isEqual(settlementTableState, state.settlementTableState)) {
            return;
        }
        const serverChanged = !isEqual(
            settlementServerFilters(state.settlementTableState.filters, effectiveState()),
            settlementServerFilters(settlementTableState.filters, effectiveState()),
        );
        update((previous) => ({
            settlementTableState,
            requestVersion: previous.requestVersion + (serverChanged && !previous.demoData ? 1 : 0),
        }));
    };
    const onRows = (rows, filters, response) => {
        if (dataReady() && response === state.data.data && isEqual(filters, state.settlementTableState.filters)) {
            update({ publication: { rows, filters, response } });
        }
    };
    const setFilter = ({ name, value }) =>
        update((state) => {
            if (["source", "isAccountsPayable"].includes(name)) {
                return settlementModeChange(state, name, value);
            }
            return {
                [name]: value,
                ...(name === "groupByDates" && value ? { groupByCounterparties: false } : {}),
                ...(name === "groupByCounterparties" && value ? { groupByDates: false } : {}),
                settlementTableState: { ...state.settlementTableState, pageIndex: 0 },
            };
        });
    const setDateValue = (name, value) =>
        update((state) => {
            const date = moment(value)[name === "startDate" ? "startOf" : "endOf"]("day");
            return {
                draftPeriod: {
                    ...state.draftPeriod,
                    [name]: date,
                    ...(name === "startDate" && date.isAfter(state.draftPeriod.endDate)
                        ? { endDate: date.clone().add(1, "day").endOf("day") }
                        : {}),
                },
            };
        });
    const applyPeriod = () => update((state) => ({ ...state.draftPeriod, requestVersion: state.requestVersion + 1 }));
    const changeDemoData = (demo) =>
        update((previous) => ({ ...settingsFor(props, demo), requestVersion: previous.requestVersion + 1 }));
    const forceUpdateReport = () => update((state) => ({ tableRevision: state.tableRevision + 1 }));
    const togglePreviousDay = () =>
        update((previous) => ({
            previousDayPaymentsOnly: !previous.previousDayPaymentsOnly,
            requestVersion: previous.requestVersion + 1,
        }));
    const onUpcomingInfoChange = (upcomingInfo) => {
        if (!isEqual(upcomingInfo, state.upcomingInfo)) {
            update({ upcomingInfo });
        }
    };
    const viewProps = () => ({
        ...props,
        ...effectiveState(),
        getItem: reload,
        requestParams: requestParams,
        settlementDataReady: dataReady(),
        onSettlementTableState: onTableState,
        onSettlementRows: onRows,
        onUpcomingInfoChange: onUpcomingInfoChange,
        togglePreviousDay: togglePreviousDay,
        setPrintTableState: setPrintTableState,
    });
    const setPrintTableState = (printTableState) => {
        printState.current = printTableState;
    };
    const actionPrint = () => {
        if (!outputReady()) {
            return;
        }
        return props.modal({
            name: "print",
            title: "Печать отчета",
            windowMod: "wide",
            CustomBody: SettlementTable,
            bodyData: { ...viewProps(), printTableState: printState.current, isPrintForm: true },
        });
    };
    const downloadExcel = async () => {
        if (!outputReady()) {
            return;
        }
        const { data, startDate, endDate } = state;
        try {
            await download({
                ...requestParams(),
                fileName: `${data.name}_${dateToView(startDate)}-${dateToView(endDate)}.xlsx`,
            });
        } catch {
            update({ error: "Не удалось выгрузить отчёт. Повторите запрос." });
        }
    };
    const paginationInfo = () => {
        if (!outputReady()) {
            return null;
        }
        const total = state.publication.rows.length;
        const { pageSize, pageIndex } = state.settlementTableState;
        const page = Math.min(pageIndex, Math.max(0, Math.ceil(total / pageSize) - 1));
        return { total, start: total ? page * pageSize + 1 : 0, end: Math.min((page + 1) * pageSize, total) };
    };
    useLayoutEffect(() => {
        loadRef.current = load;
        signatureRef.current = signature();
    });
    useEffect(() => {
        const taskKey = initialTaskKey.current;
        initialTaskKey.current = undefined;
        reload(taskKey);
    }, [reload, state.requestVersion]);
    useEffect(
        () => () => {
            requestIdRef.current += 1;
        },
        [],
    );
    useEffect(() => {
        writeSettlementState(
            sessionStorage,
            settlementStorageKey(props.profile.id, props.profile.organizationId, state.demoData),
            state,
        );
    }, [props.profile.id, props.profile.organizationId, state]);

    const { pending, error, demoData, upcomingInfo, tableRevision } = state;
    const counter = paginationInfo();
    const actions = outputReady()
        ? [
              { text: "Печать", action: actionPrint },
              ...(state.data.data.length && !isHidden(props.roles.isTruckingOrganizationDispatcher)
                  ? [{ text: "Скачать", action: downloadExcel }]
                  : []),
          ]
        : [];
    return (
        <Transition
            dir={props.transition || "full"}
            match={props.match}
            location={props.location}
            history={props.history}
        >
            <div className="page-book__show book-show">
                <div className="book-show__head">
                    <div className="book-show__close close close--sm" onClick={props.back} />
                    <div className="book-show__title book-show__head-actions flex-wrap settlements__title">
                        <span>
                            Взаиморасчёты{" "}
                            <small className="settlements__counter d-inline-block text-nowrap">
                                (Отображены{" "}
                                <strong>
                                    {counter?.total ? `${counter.start}–${counter.end}` : (counter?.total ?? "—")}
                                </strong>{" "}
                                из <strong>{counter?.total ?? "—"}</strong>)
                            </small>
                        </span>
                        <ReportDataControls demoData={demoData} onDemoChange={changeDemoData} />
                        {!demoData && (
                            <button
                                type="button"
                                className="btn btn-xs settlements__upcoming-trigger ml-3"
                                aria-haspopup="dialog"
                                aria-label="Предстоящие оплаты"
                                onClick={() =>
                                    update((state) => ({
                                        upcomingPanelRequest: state.upcomingPanelRequest + 1,
                                    }))
                                }
                                title={
                                    upcomingInfo.error
                                        ? "Не удалось обновить счётчик. Откройте панель, чтобы повторить запрос."
                                        : `На ближайшие ${upcomingInfo.days} ${ending(upcomingInfo.days, "день", "дня", "дней")}`
                                }
                            >
                                Предстоящие оплаты
                                <span className="settlements__upcoming-count ml-2" aria-live="polite">
                                    {upcomingInfo.loading ? "…" : upcomingInfo.error ? "!" : upcomingInfo.count}
                                </span>
                            </button>
                        )}
                    </div>
                    {!!actions.length && (
                        <Dropdown
                            items={actions}
                            cname="c-dropd__btn"
                            withoutChevron
                            wrapCname="book-show__dropdown c-dropd"
                        />
                    )}
                </div>
                <div className="book-show__body">
                    <div className="h-100 book-list">
                        <SettlementFilters
                            state={{ ...state, ...state.draftPeriod }}
                            setFilter={setFilter}
                            setDateValue={setDateValue}
                            getItem={reload}
                            onRefresh={applyPeriod}
                            forceUpdateReport={forceUpdateReport}
                            organizationTimezone={props.organizationTimezone}
                        />
                        {error && (
                            <div className="alert alert-danger mx-3" role="alert">
                                {error}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary ml-3"
                                    onClick={() => reload()}
                                >
                                    Повторить
                                </button>
                            </div>
                        )}
                        <div className="list__body-wrap">
                            <div className="list__body pb-3 d-flex flex-column" aria-busy={pending}>
                                <SettlementTable
                                    key={`${demoData}|${state.isAccountsPayable}|${state.source}`}
                                    {...viewProps()}
                                    tableRefreshKey={tableRevision}
                                    settlementPending={pending}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    );
}

const ConfiguredSettlements = (props) => (
    <ReportTaskGate {...props}>{(taskKey) => <MutualSettlementsPage {...props} taskKey={taskKey} />}</ReportTaskGate>
);

export default viewContainer(ConfiguredSettlements, "reports");
